import { PropsWithChildren, useMemo } from 'react';
import { createContext, useContext } from 'react';
import useLogin from '@/api/auth/useLogin.ts';
import useLogout from '@/api/auth/useLogout.ts';
import useSendResetPasswordLink from '@/api/auth/useSendResetPasswordLink.ts';
import useResetPassword from '@/api/auth/useResetPassword.ts';
import useVerifyEmail from '@/api/auth/useVerifyEmail.ts';
import useResendVerificationCode from '@/api/auth/useResendVerificationCode.ts';
import { hasAiResponsesLimit } from '@/utils/hasAiResponsesLimit.ts';
import { hasSearchLimit } from '@/utils/hasSearchLimit.ts';
import useUser from '@/api/auth/useUser.ts';
import { User } from '@/models/User';
import useCsrfCookie from '@/api/auth/useCsrfCookie.ts';

interface AuthContextProps {
  user: User | null;
  isLoading: boolean;
  loginQuery: ReturnType<typeof useLogin>;
  logoutQuery: ReturnType<typeof useLogout>;
  sendResetPasswordLinkQuery: ReturnType<typeof useSendResetPasswordLink>;
  resetPasswordQuery: ReturnType<typeof useResetPassword>;
  verifyEmailQuery: ReturnType<typeof useVerifyEmail>;
  resendVerificationCodeQuery: ReturnType<typeof useResendVerificationCode>;
  hasAiResponsesLimitFeature: boolean;
  hasSearchLimitFeature: boolean;
  availableSearches: number;
  availableAiResponses: number;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const { data: csrfCookieSet, isLoading: isLoadingCsrfCookie } = useCsrfCookie();
  const { data: user, isLoading: isLoadingUser } = useUser(csrfCookieSet);
  const loginQuery = useLogin();
  const logoutQuery = useLogout();
  const sendResetPasswordLinkQuery = useSendResetPasswordLink();
  const resetPasswordQuery = useResetPassword();
  const verifyEmailQuery = useVerifyEmail();
  const resendVerificationCodeQuery = useResendVerificationCode();

  const isLoadingAuth = isLoadingCsrfCookie || isLoadingUser;

  const hasAiResponsesLimitFeature = useMemo(
    () => (user?.features ? hasAiResponsesLimit(user?.features) : false),
    [user]
  );
  const hasSearchLimitFeature = useMemo(() => (user?.features ? hasSearchLimit(user?.features) : false), [user]);

  const availableSearches = useMemo(() => {
    return (
      (user?.features?.searches ? parseInt(user?.features.searches) : 0) +
      (user?.features?.extra_searches ? parseInt(user?.features.extra_searches) : 0)
    );
  }, [user]);

  const availableAiResponses = useMemo(() => {
    return (
      (user?.features?.ai_responses ? parseInt(user?.features.ai_responses) : 0) +
      (user?.features?.extra_ai_responses ? parseInt(user?.features.extra_ai_responses) : 0)
    );
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading: isLoadingAuth,
        loginQuery,
        logoutQuery,
        resetPasswordQuery,
        sendResetPasswordLinkQuery,
        verifyEmailQuery,
        resendVerificationCodeQuery,
        hasAiResponsesLimitFeature,
        hasSearchLimitFeature,
        availableSearches,
        availableAiResponses,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextProps {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthContext should be used within the AuthContext provider!');
  }

  return context;
}
